import { Component } from "@theme/component";
import { cmToInch, cmToInchCeil, inchToCm, formatFeetInches } from "@theme/length-units";

/**
 * Custom size slider. Emits "dimension:change" with value in cm.
 * Imperial markets work in whole inches internally, then convert back to cm.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} valueLabel
 * @property {HTMLElement} [rangeLabel]
 * @property {HTMLButtonElement} decreaseButton
 * @property {HTMLButtonElement} increaseButton
 * @property {HTMLElement} thumb
 * @property {HTMLElement} track
 *
 * @extends {Component<Refs>}
 */
export default class CustomSizeSlider extends Component {
  requiredRefs = ["valueLabel", "decreaseButton", "increaseButton", "thumb", "track"];

  /** @type {number} */
  #min = 0;

  /** @type {number} */
  #max = 0;

  /** @type {number} */
  #step = 1;

  /** @type {number} */
  #value = 0;

  /** @type {string} */
  #dimension = "width";

  /** @type {string} */
  #diameterSymbol = "";

  /** @type {boolean} */
  #imperial = false;

  connectedCallback() {
    super.connectedCallback();

    this.#imperial = this.dataset.unitSystem === "imperial";
    this.#dimension = this.dataset.dimension || "width";
    this.#diameterSymbol = this.dataset.diameterSymbol || "";

    const minCm = this.#parseNumber(this.dataset.min, 0);
    const maxCm = this.#parseNumber(this.dataset.max, minCm);
    const stepCm = this.#parseNumber(this.dataset.step, 1);
    const valueCm = this.#parseNumber(this.dataset.value, minCm);

    if (this.#imperial) {
      // Whole inches; ceil the step so it is never smaller than the cm pattern.
      this.#min = cmToInch(minCm);
      this.#max = cmToInch(maxCm);
      this.#step = Math.max(1, cmToInchCeil(stepCm));
      this.#value = cmToInch(valueCm);
    } else {
      this.#min = minCm;
      this.#max = maxCm;
      this.#step = stepCm;
      this.#value = valueCm;
    }

    if (this.#step <= 0) this.#step = 1;
    if (this.#max < this.#min) this.#max = this.#min;

    const stepsToMax = Math.floor((this.#max - this.#min) / this.#step);
    const alignedMax = this.#min + stepsToMax * this.#step;
    if (alignedMax < this.#max) {
      this.#max = alignedMax;
    }

    this.#value = this.#snap(this.#value);
    this.#render();

    // Inch-snap can change the Liquid cm value; not a shopper adjustment.
    const normalizedValueCm = this.#valueCm();
    if (normalizedValueCm !== valueCm) {
      this.dataset.value = String(normalizedValueCm);
      this.#dispatchChange({ userInitiated: false });
    }

    this.refs.thumb.addEventListener("pointerdown", this.#startDrag);
    this.refs.track.addEventListener("click", this.#handleTrackClick);
  }

  /** @param {KeyboardEvent} event */
  onKeyDown(event) {
    const bigStep = this.#step * 10;
    /** @type {Record<string, number>} */
    const deltas = {
      ArrowRight: this.#step,
      ArrowUp: this.#step,
      ArrowLeft: -this.#step,
      ArrowDown: -this.#step,
      PageUp: bigStep,
      PageDown: -bigStep
    };

    if (event.key in deltas) {
      event.preventDefault();
      this.#setValue(this.#value + deltas[event.key]);
    } else if (event.key === "Home") {
      event.preventDefault();
      this.#setValue(this.#min);
    } else if (event.key === "End") {
      event.preventDefault();
      this.#setValue(this.#max);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.thumb.removeEventListener("pointerdown", this.#startDrag);
    this.refs.track.removeEventListener("click", this.#handleTrackClick);
    this.#removeDragListeners();
  }

  decrease() {
    this.#setValue(this.#value - this.#step);
  }

  increase() {
    this.#setValue(this.#value + this.#step);
  }

  /**
   * Set the slider from a cm value (e.g. parent reset). Silent skips dimension:change
   * so callers can restore state without counting as a user adjustment.
   * @param {number} valueCm
   * @param {{ silent?: boolean }} [options]
   */
  setValueCm(valueCm, { silent = false } = {}) {
    const next = this.#imperial ? cmToInch(valueCm) : valueCm;
    this.#setValue(next, { silent });
  }

  /** @param {number} next @param {{ silent?: boolean }} [options] */
  #setValue(next, { silent = false } = {}) {
    const snapped = this.#snap(next);

    if (snapped === this.#value) {
      this.#render();
      return;
    }

    this.#value = snapped;
    this.dataset.value = String(this.#valueCm());
    this.#render();
    if (!silent) this.#dispatchChange();
  }

  /** @param {PointerEvent} event */
  #startDrag = (event) => {
    // Ignore right-click/middle-click. Touch and pen report button 0, so this doesn't affect them.
    if (!event.isPrimary || event.button !== 0) return;

    event.preventDefault();
    this.refs.thumb.classList.add("is-dragging");

    if (this.refs.thumb.setPointerCapture) {
      this.refs.thumb.setPointerCapture(event.pointerId);
    }

    this.refs.thumb.addEventListener("pointermove", this.#onDrag);
    this.refs.thumb.addEventListener("pointerup", this.#endDrag);
    this.refs.thumb.addEventListener("pointercancel", this.#endDrag);

    this.#onDrag(event);
  };

  /** @param {PointerEvent} event */
  #onDrag = (event) => {
    this.#setValueFromClientX(event.clientX);
  };

  /** @param {PointerEvent} event */
  #endDrag = (event) => {
    if (this.refs.thumb.hasPointerCapture?.(event.pointerId)) {
      this.refs.thumb.releasePointerCapture(event.pointerId);
    }

    this.refs.thumb.classList.remove("is-dragging");
    this.#removeDragListeners();
  };

  /** @param {MouseEvent} event */
  #handleTrackClick = (event) => {
    if (event.target === this.refs.thumb) return;
    this.#setValueFromClientX(event.clientX);
  };

  /** @param {number} clientX */
  #setValueFromClientX(clientX) {
    const rect = this.refs.track.getBoundingClientRect();
    const thumbSize = this.refs.thumb.getBoundingClientRect().width;
    const usable = rect.width - thumbSize;
    const x = clientX - rect.left - thumbSize / 2;
    const ratio = usable > 0 ? Math.min(1, Math.max(0, x / usable)) : 0;

    this.#setValue(this.#min + ratio * (this.#max - this.#min));
  }

  #removeDragListeners() {
    this.refs.thumb.removeEventListener("pointermove", this.#onDrag);
    this.refs.thumb.removeEventListener("pointerup", this.#endDrag);
    this.refs.thumb.removeEventListener("pointercancel", this.#endDrag);
  }

  /** @param {number} value @returns {number} */
  #snap(value) {
    if (value <= this.#min) return this.#min;
    if (value >= this.#max) return this.#max;

    const steps = Math.round((value - this.#min) / this.#step);
    const snapped = this.#min + steps * this.#step;

    if (snapped >= this.#max) return this.#max;
    if (snapped <= this.#min) return this.#min;
    return snapped;
  }

  #render() {
    const { valueLabel, rangeLabel, decreaseButton, increaseButton, thumb } = this.refs;
    const formattedValue = this.#formatValue();

    valueLabel.textContent = formattedValue;

    if (rangeLabel) {
      rangeLabel.textContent = this.#imperial
        ? `${formatFeetInches(this.#min)} – ${formatFeetInches(this.#max)}`
        : `${this.#min}–${this.#max} cm`;
    }

    const range = this.#max - this.#min;
    const progress = range > 0 ? ((this.#value - this.#min) / range) * 100 : 0;
    thumb.style.setProperty("--slider-progress", String(progress));

    thumb.setAttribute("aria-valuemin", String(this.#min));
    thumb.setAttribute("aria-valuemax", String(this.#max));
    thumb.setAttribute("aria-valuenow", String(this.#value));
    thumb.setAttribute("aria-valuetext", formattedValue);

    decreaseButton.disabled = this.#snap(this.#value - this.#step) === this.#value;
    increaseButton.disabled = this.#snap(this.#value + this.#step) === this.#value;
  }

  /** @returns {string} #value formatted with the diameter symbol (if any), shared by the visible label and aria-valuetext. */
  #formatValue() {
    return this.#imperial
      ? `${this.#diameterSymbol}${formatFeetInches(this.#value)}`
      : `${this.#diameterSymbol}${this.#value} cm`;
  }

  /** @param {{ userInitiated?: boolean }} [options] */
  #dispatchChange({ userInitiated = true } = {}) {
    this.dispatchEvent(
      new CustomEvent("dimension:change", {
        bubbles: true,
        detail: {
          dimension: this.#dimension,
          value: this.#valueCm(),
          userInitiated
        }
      })
    );
  }

  /** @returns {number} #value converted to cm — always cm so price/cart stay metric. */
  #valueCm() {
    return this.#imperial ? inchToCm(this.#value) : this.#value;
  }

  /** @param {string | undefined} raw @param {number} fallback @returns {number} */
  #parseNumber(raw, fallback) {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
}

if (!customElements.get("custom-size-slider")) {
  customElements.define("custom-size-slider", CustomSizeSlider);
}
